import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Lock, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO_URL } from "@/lib/brand";
import Seo from "@/components/Seo";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (user && user.role === "admin") navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const { ok, error } = await login(form.email, form.password);
    setSubmitting(false);
    if (ok) {
      toast.success("Welcome back, admin.");
      const dest = location.state?.from?.pathname || "/admin";
      navigate(dest, { replace: true });
    } else {
      toast.error(error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-cream flex" data-testid="login-page">
      <Seo title="Sign in" noindex />
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.pexels.com/photos/37667517/pexels-photo-37667517.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
          alt="Taj Mahal"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-deepred/60 via-deepred/40 to-deepred/80" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="TTI Holidays" className="h-16 w-auto rounded-sm" />
          </Link>
          <div>
            <div className="eyebrow text-marigold mb-3">Welcome back</div>
            <h2 className="font-serif text-5xl tracking-tight leading-[1.05] max-w-md">
              Sign in to manage tours, inquiries & reviews.
            </h2>
          </div>
          <div className="text-xs text-white/60">© {new Date().getFullYear()} TTI Holidays · Agra, India</div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10 flex items-center gap-3">
            <img src={LOGO_URL} alt="TTI Holidays" className="h-14 w-auto rounded-sm" />
          </div>
          <div className="eyebrow mb-3">Sign in</div>
          <h1 className="font-serif text-4xl tracking-tight mb-2">Admin Console</h1>
          <p className="text-foreground/70 mb-8 font-light">Access restricted to TTI team members.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  required
                  data-testid="login-email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="pl-10 bg-white h-11"
                  placeholder="admin@ttiholidays.com"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  required
                  data-testid="login-password"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  className="pl-10 bg-white h-11"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="btn-primary w-full h-12 disabled:opacity-60"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…</> : "Sign In"}
            </button>
          </form>

          <div className="mt-10 text-xs text-center text-foreground/60">
            <Link to="/" className="hover:text-saffron">← Back to website</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
